import { BlockPeriodTypeEnum } from '../../enums/meal-plan/block-period-type.enum';
import {
  _DEFAULT_NUTRITIONAL_PROPERTIES,
  IFoodGroup,
  IFoodGroupIngredient,
  INutritionalProperties
} from '../../models/food-group';
import { cloneDeep } from 'lodash';
import { IBlockPeriod } from '../../models/meal-plans';
import { AnyObject } from 'immer/dist/internal';

export class BlockPeriodClass {
  // tslint:disable:variable-name
  _id: string;
  mealPlanId: string;
  title: string;
  type: BlockPeriodTypeEnum;
  hasHour: boolean;
  time: { hour: number, minute: number };
  datetime: Date;
  index: number;
  nutritionalProperties: INutritionalProperties;
  ingredients: Partial<IFoodGroupIngredient>[];
  foodGroupId: string;
  foodGroup: IFoodGroup;
  text: string; // aplica cuando es de tipo instrucción y cuando es de tipo food group aquí vendrá la descripción del platillo
  isCustomFoodGroup: boolean;
  foodGroupTitle: string;
  editMode: boolean;
  backup?: IBlockPeriod;
  ingredientToEdit?: Partial<IFoodGroupIngredient>;
  isNewBlockPeriod?: boolean;
  modeReadOnly?: boolean;
  valid?: boolean;
  isAllergic?: boolean;
  metadata: AnyObject = {
    distribution: {},
    nutrientTable: {}
  };

  // Corregir
  childBlocksPeriod: IBlockPeriod[] | BlockPeriodClass[] = [];


  constructor(blockPeriod?: IBlockPeriod, id?: string, isNew?: boolean) {
    this._id = blockPeriod?._id ? blockPeriod._id : id ? id : null;
    this.mealPlanId = blockPeriod?.mealPlanId ?? '';
    this.title = blockPeriod?.title ?? 'Nuevo';
    this.type = blockPeriod?.type ?? BlockPeriodTypeEnum.INSTRUCTION;
    this.hasHour = blockPeriod?.hasHour ?? false;
    this.time = blockPeriod?.time ?? { hour: null, minute: null };
    this.datetime = blockPeriod?.datetime ?? null;
    this.index = blockPeriod?.index ?? 0;
    this.nutritionalProperties = blockPeriod?.nutritionalProperties ?? cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    this.ingredients = blockPeriod?.ingredients ?? [];
    this.foodGroupId = blockPeriod?.foodGroupId ?? '';
    this.foodGroup = blockPeriod?.foodGroup ?? null;
    this.text = blockPeriod?.text ?? '';
    this.isCustomFoodGroup = blockPeriod?.isCustomFoodGroup ?? false;
    this.foodGroupTitle = blockPeriod?.foodGroupTitle ?? '';
    this.editMode = false;
    this.isNewBlockPeriod = isNew;
    this.modeReadOnly = false;
    this.metadata = blockPeriod?.metadata || { distribution: {}, nutrientTable: {} };
  }

  setId(value: string): void {
    this._id = value;
  }

  setMealPlanId(value: string): void {
    this.mealPlanId = value;
  }

  setTitle(value: string): void {
    this.title = value;
  }

  setType(value: BlockPeriodTypeEnum): void {
    this.type = value;
  }

  setHasHour(value: boolean): void {
    this.hasHour = value;
  }

  setTime(value: { hour: number; minute: number }): void {
    this.time = value;
  }

  setDatetime(value: Date): void {
    this.datetime = value;
  }

  setIndex(value: number): void {
    this.index = value;
  }

  setIsAllergic(value: boolean): void {
    this.isAllergic = value;
  }

  setNutritionalProperties(value: INutritionalProperties): void {
    this.nutritionalProperties = value;
  }

  setIngredients(value: Partial<IFoodGroupIngredient>[]): void {
    this.ingredients = value;
  }

  setFoodGroupId(value: string): void {
    this.foodGroupId = value;
  }

  setFoodGroup(value: IFoodGroup): void {
    this.foodGroup = value;
  }

  setText(value: string): void {
    this.text = value;
  }

  setIsCustomFoodGroup(value: boolean): void {
    this.isCustomFoodGroup = value;
  }

  setFoodGroupTitle(value: string): void {
    this.foodGroupTitle = value;
  }

  setEditMode(value: boolean): void {
    try {
      this.editMode = value;
      if (this.editMode) {
        this.copyToBackup();
      } else {
        this.backup = null;
      }
    } catch (e) {
      console.log(e);
    }
  }

  setIsNewBlock(value: boolean): void {
    this.isNewBlockPeriod = value;
  }

  setModeReadOnly(value: boolean): void {
    this.modeReadOnly = value;
  }

  copyToBackup(): void {
    this.backup = cloneDeep({
      _id: this._id,
      mealPlanId: this.mealPlanId,
      title: this.title,
      type: this.type,
      hasHour: this.hasHour,
      time: this.time,
      datetime: this.datetime,
      index: this.index,
      nutritionalProperties: this.nutritionalProperties,
      ingredients: this.ingredients,
      foodGroupId: this.foodGroupId,
      foodGroup: this.foodGroup,
      text: this.text,
      isCustomFoodGroup: this.isCustomFoodGroup,
      foodGroupTitle: this.foodGroupTitle,
      metadata: this.metadata,
      childBlocksPeriod: this.childBlocksPeriod
    });
  }

  addIngredient(ingredient: Partial<IFoodGroupIngredient>): void {
    // tslint:disable:max-line-length
    const hasIngredientIndex = this.ingredients.findIndex(item => item.ingredientId === ingredient.ingredientId);
    if (hasIngredientIndex !== -1) {
      // se encuentra un ingrediente igual en el arreglo
      if (ingredient.measure._id !== this.ingredients[hasIngredientIndex].measure._id) {
        let gramsOld = this.ingredients[hasIngredientIndex].units;
        if (this.ingredients[hasIngredientIndex].measure.symbol !== 'g') {
          gramsOld = this.convertToGrams(gramsOld, ingredient.nutritionalProperties.netWeight, ingredient.nutritionalProperties.suggestedQuantity);
        }
        let gramsNew = ingredient.units;
        if (ingredient.measure.symbol_es !== 'g') {
          gramsNew = this.convertToGrams(gramsNew, ingredient.nutritionalProperties.netWeight, ingredient.nutritionalProperties.suggestedQuantity);
        }
        this.ingredients[hasIngredientIndex].measure = ingredient.measure.symbol_es === 'g' ? ingredient.measure : this.ingredients[hasIngredientIndex].measure;
        this.ingredients[hasIngredientIndex].units = gramsOld + gramsNew;
        return;
      }
      this.ingredients[hasIngredientIndex].units += ingredient.units;
      return;
    }
    this.ingredients.push(ingredient);
  }

  convertToGrams(units: number, netWeight: number, suggestedQuantity: number): number {
    return (units * netWeight) / suggestedQuantity;
  }

  editIngredient(ingredient: Partial<IFoodGroupIngredient>, ingredientId: string): void {
    const index = this.ingredients.findIndex(_ingredient => _ingredient.ingredientId === ingredientId);
    if (index !== -1) {
      this.ingredients[index] = ingredient;
    }
  }

  deleteIngredient(ingredientId: string): void {
    const index = this.ingredients.findIndex(_ingredient => _ingredient.ingredientId === ingredientId);
    if (index !== -1) {
      this.ingredients.splice(index, 1);
    }
  }

  resetData(): void {
    this.nutritionalProperties = cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    this.ingredients = [];
    this.foodGroupId = '';
    this.foodGroup = null;
    this.text = '';
    this.isCustomFoodGroup = false;
    this.foodGroupTitle = '';
  }

  /**
   * Restar los nutrientes que se le pasen a los nutrientes del blockPeriod
   */
  subtractNutrients(nutrients: Partial<INutritionalProperties>): void {
    Object.keys(this.nutritionalProperties).forEach(key => {
      if (this.ingredients.length > 0) {
        this.nutritionalProperties[key] = +(this.nutritionalProperties[key] - (nutrients[key] !== -1 ? nutrients[key] : 0)).toFixed(2);
      } else {
        this.nutritionalProperties[key] = 0;
      }
    });
  }

  /**
   * sumar los nutrientes que se le pasen a los nutrientes del blockPeriod
   */
  sumNutrients(nutrients: Partial<INutritionalProperties>): void {
    Object.keys(this.nutritionalProperties).forEach(key => {
      if (this.ingredients.length > 0) {
        this.nutritionalProperties[key] = +(this.nutritionalProperties[key] + (nutrients[key] !== -1 ? nutrients[key] : 0)).toFixed(2);
      } else {
        this.nutritionalProperties[key] = 0;
      }
    });
  }


  isValid(): boolean {

    this.valid = false;

    if (!this.title) {
      this.valid = false;
      return this.valid;
    }


    if (this.type === BlockPeriodTypeEnum.INSTRUCTION && this.text.length > 0) {
      this.valid = true;
      return this.valid;
    }

    if ((this.type === BlockPeriodTypeEnum.INDIVIDUAL || this.type === BlockPeriodTypeEnum.FOOD_GROUP) && this.ingredients.length > 0) {
      this.valid = true;
      return this.valid;
    }

    return this.valid;
  }

  hasType(): boolean {
    return !!(this.type && this.title);
  }
}
